import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import DateIconButton from 'components/v2/buttons/DateIconButton';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import { getRequestFields } from './accessorialRequestPaneUtils';
import { getLabelInfo, getMomentDate, DATE_FORMAT, locationTypes, } from './editFormHelpers';
const EditAccessorialForm = ({ referenceAccessorial, accessorial, isCarrier, onChange, onSave, onCancel, }) => {
    const { t } = useTranslation();
    const [label, info] = getLabelInfo(referenceAccessorial);
    const [errors, setErrors] = useState({
        carrier_rate: null,
        shipper_rate: null,
        carrier_unit_qty: null,
        shipper_unit_qty: null,
    });
    const { rateField, unitsField, beginDateField, endDateField, locationField, descriptionField, notesField, rate, units, beginDate, endDate, location, description, notes, } = getRequestFields(accessorial, isCarrier);
    function handleErrors(field, value) {
        setErrors((prev) => {
            let previous = Object.assign({}, prev);
            // Payee rates can be negative
            if (field === 'carrier_rate') {
                previous[field] = null;
            }
            else if (field === 'shipper_rate') {
                let parsedValue = parseFloat(value);
                previous[field] =
                    isNil(parsedValue) || parsedValue < 0
                        ? t('VALUE_MUST_BE_A_NUMBER_AND_CANNOT_BE_NEGATIVE')
                        : null;
            }
            else {
                let parsedValue = parseFloat(value);
                previous[field] =
                    !parsedValue || parsedValue < 0
                        ? t('VALUE_MUST_BE_A_NUMBER_AND_CANNOT_BE_NEGATIVE')
                        : null;
            }
            return previous;
        });
    }
    function handleChange(field) {
        return function (value) {
            const locationValueFields = ['shipper_location', 'location'];
            const positiveValueFields = [
                'carrier_rate',
                'shipper_rate',
                'carrier_unit_qty',
                'shipper_unit_qty',
            ];
            if (positiveValueFields.includes(field)) {
                handleErrors(field, value);
                onChange(field, value);
            }
            else if (locationValueFields.includes(field)) {
                onChange(field, value.value);
            }
            else {
                onChange(field, value);
            }
        };
    }
    function handleSave() {
        let errorsValidated = true;
        const errorsToCheck = Object.keys(errors);
        for (let i = 0; i < errorsToCheck.length; i++) {
            const item = errorsToCheck[i];
            if (errors[item]) {
                errorsValidated = false;
                break;
            }
        }
        if (errorsValidated)
            onSave();
    }
    const showBeginDate = referenceAccessorial.begin_date_required || beginDate;
    const showEndDate = referenceAccessorial.end_date_required || endDate;
    const showLocation = referenceAccessorial.location_required || location;
    const showDescription = referenceAccessorial.description_required || description;
    return (React.createElement("div", { "data-testid": "div-7da91118", className: "p-4 bg-gray-50" },
        React.createElement("div", { "data-testid": "div-1d3de2e3", className: "flex mb-4 -mx-2" },
            React.createElement("div", { "data-testid": "div-b0b4eef1", className: "w-1/3 px-2 mb-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a3020910", value: units, type: "number", label: t(label), info: t(info), placeholder: t('UNITS_PLACEHOLDER'), onChange: handleChange(unitsField), maxLength: 35 }),
                React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-ea22ccb6", className: "mt-1" }, errors[unitsField])),
            React.createElement("div", { "data-testid": "div-e4f53b07", className: "px-2 mb-4 w-1/3" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-701ee0c0", value: rate, type: "number", label: t('RATE_PER_UNIT'), info: t('ACCESSORIAL_RATE'), placeholder: t('RATE', { count: 1 }), onChange: handleChange(rateField), size: "20" }),
                React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-9305067f", className: "mt-1" }, errors[rateField]))),
        React.createElement("div", { "data-testid": "div-103a7e17", className: "flex flex-wrap -mx-2" },
            showBeginDate && (React.createElement("div", { "data-testid": "div-824ddee0", className: "w-1/3 px-2 mb-4" },
                React.createElement("label", { "data-testid": "label-c692eeb5", htmlFor: "beginDate", className: "block mb-1 form-label" }, t('BEGIN_DATE')),
                React.createElement(DatePicker, { id: "beginDate", selected: getMomentDate(beginDate), onChange: handleChange(beginDateField), customInput: React.createElement(DateIconButton, { dataTestId: "DateIconButton-117a097b" }), shouldCloseOnSelect: true, dateFormat: DATE_FORMAT, showTimeSelect: true, timeIntervals: 5 }))),
            showEndDate && (React.createElement("div", { "data-testid": "div-80400f2a", className: "w-1/3 px-2 mb-4" },
                React.createElement("label", { "data-testid": "label-36458e54", htmlFor: "endDate", className: "block mb-1 form-label" }, t('END_DATE')),
                React.createElement(DatePicker, { id: "endDate", selected: getMomentDate(endDate), onChange: handleChange(endDateField), customInput: React.createElement(DateIconButton, { dataTestId: "DateIconButton-f19e3a72" }), dateFormat: DATE_FORMAT, shouldCloseOnSelect: true, showTimeSelect: true, timeIntervals: 5 }))),
            showLocation && (React.createElement("div", { "data-testid": "div-5af39733", className: "w-1/3 px-2 mb-4" },
                React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-a9729c71", selected: location, fullWidth: false, items: locationTypes, defaultSelectLabel: "", label: t('LOCATION'), info: t('LOCATION_CUSTOMER_OR_TERMINAL'), onSelect: handleChange(locationField), showCaption: false, valueKey: "value", textKey: "localizationSlug" })))),
        showDescription && (React.createElement("div", { "data-testid": "div-2fba806f", className: "flex -mx-2" },
            React.createElement("div", { "data-testid": "div-8c413d30", className: "w-1/2 px-2 mb-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a9b1f3b2", value: description, type: "text", label: t('DESCRIPTION', { count: 1 }), placeholder: t('DESCRIPTION', { count: 1 }), onChange: handleChange(descriptionField) })))),
        React.createElement("div", { "data-testid": "div-3ea69e7d", className: "w-1/2 mb-4" },
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a8d3aebf", value: notes, label: titleize(t('NOTE', { count: 3 })), info: titleize(t('NOTE', { count: 3 })), type: "textarea", placeholder: titleize(t('NOTE', { count: 3 })), onChange: handleChange(notesField) })),
        React.createElement("div", { "data-testid": "div-55bd5128", className: "mt-6" },
            React.createElement("div", { "data-testid": "div-d855d4f3", className: "flex items-center justify-end -mx-2" },
                React.createElement("div", { "data-testid": "div-3e92eaad", className: "px-2" },
                    React.createElement("button", { "data-testid": "button-70262908", type: "button", className: "button button-green", title: titleize(t('SAVE')), onClick: handleSave }, titleize(t('SAVE')))),
                React.createElement("div", { "data-testid": "div-65cb51ad", className: "px-2" },
                    React.createElement("button", { "data-testid": "button-0d6cf1a1", type: "button", title: t('CANCEL_EDIT'), className: "button button-secondary", onClick: onCancel }, t('CANCEL')))))));
};
export default EditAccessorialForm;
